import React from 'react';
import { CornerUpLeft, CornerUpRight, Loader2, Send, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import ReplyAsSelector from './ReplyAsSelector';
import ContactEmailTypeahead from '../ContactEmailTypeahead';
import { CcBccFields, CcBccToggle } from './inboxUtils';
import RichTextEditor from '../RichTextEditor';
import TemplateSignatureSelectors from './TemplateSignatureSelectors';
import AttachmentBar from './AttachmentBar';
import { modeContract, formatModeText, showsRecipients, showsSubject, subjectMode } from './mailComposerModes';
import { MAIL_TOOLBAR } from './editorPresets';
import { hasAuthoredText } from '../../lib/editorSafeHtml';

/**
 * MailComposer — one composer, driven by a mode (#623).
 *
 * The mode contract is DATA, in `mailComposerModes.js`, not conditionals spread
 * through this file: header, recipients, subject, placeholder and send label all
 * come from one row of that table. #623 asks that a mode needing more be handled
 * by widening the table rather than forking the composer, and keeping it as data
 * is what makes that possible.
 *
 * ## What this component is, and is not
 *
 * It is the shared MIDDLE of every mail surface — recipients, subject, the
 * template/signature slot, the editor and attachments, plus the identity row
 * (page) or the send row (thread cards). It is NOT the page chrome:
 * `ComposePage` and `PersonalComposePage` keep their own headers, footers and
 * navigation, and `ReplyComposer` keeps the outer card's border and margins.
 *
 * ## Three layouts, one prop (`variant`)
 *
 * | `variant` | used by | identity row | mode header | send row |
 * |-----------|---------|--------------|-------------|----------|
 * | `page`    | both full-page compose screens | yes (From) | — (compose has none) | no — the page's own footer |
 * | `card`    | the anchored ↩ reply (#659) | no — the reader footer's | `#FBFAFE` row + ✕ | Discard · Send |
 * | `thread`  | the thread-level Reply all (#659) | no — the reader footer's | white row + ✕ | Discard · Send |
 *
 * The layout decides chrome; the mode contract still decides which FIELDS
 * render. Until #659 there was a fourth, stacked `inline` layout and a
 * separate `chrome` prop; nothing used the stacked layout once the thread
 * cards moved to `card`/`thread`, so both were folded into this one enum.
 *
 * ## Purely presentational — no data hooks
 *
 * Same constraint as `TemplateSignatureSelectors`, for the same reason: this
 * renders inside `messagesWithBodies.map(...)` on the thread surfaces, once per
 * message. `useEmailTemplates`/`useSignatures` here would break the rules of
 * hooks (`craco.config.js` makes that an `error`, so it fails `yarn build`) and
 * multiply each hook's mount-time `invalidateQueries` by the thread length.
 *
 * ## Send is a plain button
 *
 * Scheduled send does not exist anywhere in this product — no `send_at`, no
 * `sendLater`, no `scheduled_send`. Per #623's own instruction, Send ships as a
 * plain button rather than a split control wrapped around a single item. Do not
 * add an empty menu.
 *
 * ## Test ids are injected
 *
 * Each surface passes its historical ids through `testIds` so the assertions
 * that already exist keep testing the thing they were written for. A composer
 * that silently renamed its ids would make every one of them vacuous.
 */
export default function MailComposer({
    mode = 'compose',
    composerId = 'composer',
    testIds = {},
    /**
     * Fills `{name}` / `{subject}` / `{n}` in the mode's header and subject
     * template. The strings are the product's explanation of the threading
     * model — a reply STAYS on the thread, reply-all and forward start a new
     * one — so they are taken verbatim from `mailComposerModes` rather than
     * paraphrased at each call site.
     */
    modeVars = {},

    // ── Sending identity (page only) ────────────────────────────────────
    // Read from the SAME source the send path uses (`inboxRules.fromOptions` /
    // `defaultFrom` on the departmental lane, `usePersonalSenders` on the
    // personal one). Never a parallel implementation, and never inferred from
    // the address domain — a custom domain on Gmail would be labelled wrong.
    // The thread cards draw no identity: their From is the reader footer's
    // (`ThreadFooter`, #659 D3).
    options = [],
    from,
    onFromChange,
    renderBadge,
    // Shown by ReplyAsSelector when there are no options at all. The personal
    // lane needs it: until its two status queries resolve there ARE no options,
    // and "No mailbox connected" would be a claim the composer cannot yet make.
    emptyHint,
    // Rendered directly under the identity row (e.g. "Calendar is not connected
    // for this mailbox — sending is unaffected").
    identityNote,

    // ── Recipients ──────────────────────────────────────────────────────
    to = [], onToChange,
    cc = [], onCcChange,
    bcc = [], onBccChange,
    // The thread cards' single "CC / BCC" disclosure: a card has one narrow
    // To row and no space for two toggles.
    showCcBcc = false, onToggleCcBcc,
    /**
     * The page variant reveals Cc and Bcc INDEPENDENTLY, which is what the
     * design draws and what every mail client does — `Cc` and `Bcc` are two
     * words in the To row, each opening its own labelled row with its own
     * remove control. One shared boolean could not express "Bcc only", and
     * forcing an empty Cc row onto someone who asked for Bcc is the kind of
     * thing that gets a stray address left in a sent message.
     */
    showCc = false, onToggleCc,
    showBcc = false, onToggleBcc,

    // ── Subject: an input where `subjectMode` is 'fill', a read-only line
    //    where the mode shows one (reply all), nothing otherwise ──────────
    subject = '', onSubjectChange,

    // ── Body ────────────────────────────────────────────────────────────
    body = '', onBodyChange,
    editorRef,
    // The editor toolbar preset (#624 N6). Defaults to the REPLY bar. A
    // full-page compose surface that turns on merge variables or the content
    // library must pass MAIL_COMPOSE_TOOLBAR, or the preset filters those two
    // controls straight back out from under it.
    toolbar = MAIL_TOOLBAR,
    showMergeVariables = false,
    showAIWriter = false,
    showContentLibrary = false,

    // ── Template / signature slot ───────────────────────────────────────
    templates, adminTemplates, signatures, adminSignatures, activeOverrideId,
    user, senderEmail,
    signature, onSignatureChange,
    selectorsDisabled = false,

    // ── Attachments ─────────────────────────────────────────────────────
    attachments, onAddAttachments, onRemoveAttachment,

    // ── Draft status (#623 Phase 7) ─────────────────────────────────────
    draftSavedAt,
    onDiscard,

    // ── Close (✕ in the mode header), thread surfaces only ──────────────
    onClose,

    // ── Send (thread cards only) ────────────────────────────────────────
    // The full-page surfaces keep their own footer — it carries surface-
    // specific controls (the Track toggle, the provider status indicator,
    // Cancel) and its own long-standing test ids — so `page` draws no send row.
    onSend,
    sending = false,
    sendDisabled = false,

    // ── Banners ─────────────────────────────────────────────────────────
    reconnectRequired = false,
    reconnectHref = '/admin/unified-inbox',

    /**
     * `card` (the default), `thread` or `page` — see the table at the top.
     * The caller supplies a card's own border and margins in `className`.
     */
    variant = 'card',
    /** The sending mailbox, so the suggestion menu can mark external contacts. */
    ownDomain = '',
    className = '',
}) {
    const contract = modeContract(mode);
    // #970 — Send waits for typed text; the signature block alone is not a message.
    const authored = hasAuthoredText(body);
    const subjMode = subjectMode(contract);
    const id = key => testIds[key] || `${key}-${composerId}`;
    const page = variant === 'page';
    // One hairline colour per card: the anchored card's is lavender, the
    // thread-level card's (and the page's) the neutral warm grey.
    const RULE = variant === 'card' ? 'border-[#EDEBF9]' : 'border-[#EDEBE6]';
    // The design's row: 20px gutter, 44px tall, one hairline underneath. `py`
    // rather than a fixed height on the recipient rows, because a wrapped list
    // of chips has to be able to grow.
    const ROW = 'flex items-center gap-[14px] px-5 border-b border-[#EDEBE6]';
    const LABEL = 'w-[54px] flex-shrink-0 text-[12.5px] font-normal text-[#8A837E]';

    // `formatModeText` leaves an unfilled token in place, literally, so a
    // missing `{n}` shows as "{n}" rather than shipping "Forward entire thread
    // ()" — a confident, wrong statement about what is about to be sent.
    const modeHeader = formatModeText(contract.header, modeVars);
    const ModeIcon = mode === 'forward' ? CornerUpRight : CornerUpLeft;

    return (
        <div
            className={page ? `flex flex-col flex-1 min-h-0 ${className}` : className}
            data-testid={id('mail-composer')}
        >
            {/* The mode header — the line §13.3 specifies and nothing rendered.
                Without it Reply, Reply all and Forward were visually identical
                and the user had no way to tell which one they were in, or that
                two of the three start a NEW thread. */}
            {modeHeader && (
                <div
                    className={page
                        ? 'flex items-center gap-2 px-5 py-3 border-b border-[#EDEBE6]'
                        : variant === 'card'
                            ? `flex items-center gap-[10px] px-[14px] py-[10px] bg-[#FBFAFE] border-b ${RULE}`
                            : `flex items-center gap-[10px] px-[14px] py-[11px] border-b ${RULE}`}
                    data-testid={id('composer-mode-header')}
                >
                    <ModeIcon
                        className={`w-[15px] h-[15px] flex-shrink-0 ${
                            variant === 'card' ? 'text-[#4D11F6]' : 'text-[#57504B]'}`}
                    />
                    <span className="text-[12.5px] font-semibold text-[#0C0A09]">
                        {modeHeader}
                    </span>
                    {contract.annotation && (
                        /* In-thread is plain muted text; new-thread is the
                           prototype's neutral pill. Two treatments because they
                           say opposite things about where the message lands. */
                        <span
                            className={`flex-shrink-0 ${
                                contract.threadBehaviour === 'new-thread'
                                    ? 'text-[11px] font-medium px-[7px] py-[2px] rounded-[5px] '
                                      + 'text-[#57504B] bg-[#F1EFEA] border border-[#E4E1DB]'
                                    : 'text-[11.5px] text-[#8A837E]'
                            }`}
                            data-testid={id('composer-mode-annotation')}
                        >
                            {contract.annotation}
                        </span>
                    )}
                    {onClose && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="ml-auto flex items-center justify-center w-6 h-6 rounded-[6px]
                                       text-[#8A837E] hover:bg-[#F4F2EE] hover:text-[#57504B] flex-shrink-0"
                            aria-label="Close composer"
                            data-testid={id('reply-composer-close')}
                        >
                            <X className="w-[15px] h-[15px]" />
                        </button>
                    )}
                </div>
            )}

            {reconnectRequired && (
                <div className={`rounded-md bg-amber-50 border border-amber-200 p-3 ${page ? 'mx-5 mt-3' : 'mx-[14px] my-3'}`}>
                    <p className="text-sm font-medium text-amber-800">Reconnection required</p>
                    <p className="text-sm text-amber-700 mt-1">
                        Your mailbox connection has expired. Please re-authorize to continue sending emails.
                    </p>
                    <a href={reconnectHref} className="text-sm text-amber-800 underline mt-2 inline-block">
                        Go to Settings →
                    </a>
                </div>
            )}

            {/* Identity row — `page` only, as the design's first hairline row:
                "From   addr ⌄" with the label in the gutter, so the selector
                renders bare and the label belongs to the row, not the control.

                The thread cards draw none: their From choice is the reader
                footer's "Replying as" (#659 D3), and the Reply ↔ Reply All
                toggle that used to share this row is gone (#659 D4) — Reply all
                opens from the footer or the `⋯` menu. */}
            {page && (
                <div className={`${ROW} h-11`}>
                    <span className={LABEL}>From</span>
                    <ReplyAsSelector
                        options={options}
                        value={from}
                        onChange={onFromChange}
                        label="From"
                        renderBadge={renderBadge}
                        variant="inline"
                        {...(emptyHint ? { emptyHint } : {})}
                    />
                </div>
            )}
            {identityNote}

            {/* Recipients.

                Thread cards (#509): the single CC/BCC toggle lives in the To
                header row and the inputs are a SIBLING block below it —
                rendering both inside the header row squeezed CC/BCC into the
                top-right corner above the To field.

                Page variant: the design's three rows. `Cc` and `Bcc` are
                separate words in the To row, each revealing its own labelled
                row with its own remove control. */}
            {showsRecipients(contract) && (page ? (
                <>
                    <div className={`${ROW} py-1.5 min-h-[44px]`}>
                        <span className={LABEL}>To</span>
                        <ContactEmailTypeahead
                            value={to}
                            onChange={onToChange}
                            placeholder="Add recipient..."
                            variant="bare"
                            suggestOnFocus
                            ownDomain={ownDomain}
                        />
                        {/* Both words stay in the row once their field is open,
                            as the design draws them — the To row's shape does
                            not change under you while you are typing into it.
                            Opening an already-open field is a no-op; CLOSING is
                            the row's own X, next to the addresses it discards. */}
                        <button
                            type="button"
                            onClick={() => onToggleCc?.(true)}
                            className="text-[12.5px] text-[#57504B] hover:text-[#0C0A09] flex-shrink-0"
                            aria-expanded={showCc}
                            aria-controls={`cc-${composerId}`}
                            data-testid={id('cc-toggle')}
                        >
                            Cc
                        </button>
                        <button
                            type="button"
                            onClick={() => onToggleBcc?.(true)}
                            className="text-[12.5px] text-[#57504B] hover:text-[#0C0A09] flex-shrink-0"
                            aria-expanded={showBcc}
                            aria-controls={`bcc-${composerId}`}
                            data-testid={id('bcc-toggle')}
                        >
                            Bcc
                        </button>
                    </div>

                    {showCc && (
                        <div id={`cc-${composerId}`} className={`${ROW} py-1.5 min-h-[44px]`} data-testid={id('cc-row')}>
                            <span className={LABEL}>Cc</span>
                            <ContactEmailTypeahead
                                value={cc}
                                onChange={onCcChange}
                                placeholder="Add Cc recipient..."
                                variant="bare"
                                suggestOnFocus
                                ownDomain={ownDomain}
                            />
                            {/* Hiding the row CLEARS it. A hidden Cc that still
                                carries addresses sends them — the one failure
                                mode a disclosure control must not have. */}
                            <button
                                type="button"
                                title="Remove Cc"
                                aria-label="Remove Cc"
                                onClick={() => { onCcChange?.([]); onToggleCc?.(false); }}
                                className="flex items-center justify-center w-[26px] h-[26px] rounded-[7px]
                                           text-[#8A837E] hover:bg-[#F4F2EE] hover:text-[#57504B] flex-shrink-0"
                            >
                                <X className="w-[15px] h-[15px]" />
                            </button>
                        </div>
                    )}

                    {showBcc && (
                        <div id={`bcc-${composerId}`} className={`${ROW} py-1.5 min-h-[44px]`} data-testid={id('bcc-row')}>
                            <span className={LABEL}>Bcc</span>
                            <ContactEmailTypeahead
                                value={bcc}
                                onChange={onBccChange}
                                placeholder="Add Bcc recipient..."
                                variant="bare"
                                suggestOnFocus
                                ownDomain={ownDomain}
                            />
                            <button
                                type="button"
                                title="Remove Bcc"
                                aria-label="Remove Bcc"
                                onClick={() => { onBccChange?.([]); onToggleBcc?.(false); }}
                                className="flex items-center justify-center w-[26px] h-[26px] rounded-[7px]
                                           text-[#8A837E] hover:bg-[#F4F2EE] hover:text-[#57504B] flex-shrink-0"
                            >
                                <X className="w-[15px] h-[15px]" />
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <div className="px-[14px]">
                    {/* One ROW per field — label at a fixed gutter, control
                        filling the rest, hairline under. The stacked
                        label-above-box layout belonged to a form; a composer
                        header reads as a letter, which is what the design
                        draws and what every mail client does. */}
                    <div className={`flex items-center gap-3 border-b py-1.5 ${RULE}`}>
                        <Label className="w-[68px] flex-shrink-0 text-xs text-stone-500">To</Label>
                        <div className="flex-1 min-w-0">
                            <ContactEmailTypeahead
                                value={to}
                                onChange={onToChange}
                                placeholder="Add recipient…"
                            />
                        </div>
                        <CcBccToggle
                            show={showCcBcc}
                            onToggle={() => onToggleCcBcc?.(!showCcBcc)}
                            controlsId={`cc-bcc-${composerId}`}
                        />
                    </div>
                    {/* In a card the rows are full-bleed, so the open Cc/Bcc
                        block needs its own spacing and rule. */}
                    <div className={showCcBcc ? `py-2 border-b ${RULE}` : ''}>
                    <CcBccFields
                        id={`cc-bcc-${composerId}`}
                        show={showCcBcc}
                        cc={cc}
                        onCcChange={onCcChange}
                        bcc={bcc}
                        onBccChange={onBccChange}
                        TypeaheadComponent={ContactEmailTypeahead}
                        placeholder="Add"
                    />
                    </div>
                </div>
            ))}

            {/* Subject — only the modes whose `subjectTemplate` is not null. A
                reply's subject is the thread's and is never shown here. An
                editable mode gets an input; a read-only one (reply all, as the
                prototype draws it) gets the derived subject as text, the
                caller's `subject` — the same value its send payload carries.
                The page variant also parks the draft stamp here, where the
                design puts it: on the last header row, not in the footer. */}
            {showsSubject(contract) && (
                <div className={page
                    ? `${ROW} h-11`
                    : `flex items-center gap-3 px-[14px] border-b py-1.5 min-h-[40px] ${RULE}`}
                >
                    {page ? (
                        <span className={LABEL}>Subject</span>
                    ) : (
                        <Label className="w-[68px] flex-shrink-0 text-xs text-stone-500">Subject</Label>
                    )}
                    {subjMode !== 'fill' ? (
                        <span
                            className="flex-1 min-w-0 truncate text-[13px] text-[#0C0A09]"
                            data-testid={id('composer-subject')}
                            data-readonly="true"
                        >
                            {subject}
                        </span>
                    ) : (
                    <Input
                        value={subject}
                        onChange={e => onSubjectChange?.(e.target.value)}
                        placeholder="Add a subject"
                        className={`flex-1 min-w-0 border-0 shadow-none rounded-none px-0
                            focus-visible:ring-0 bg-transparent text-[#0C0A09] placeholder:text-[#A29B95]
                            ${page ? 'h-full text-[13.5px]' : 'h-8 text-[13px]'}`}
                        data-testid={id('composer-subject')}
                    />
                    )}
                    {page && draftSavedAt && (
                        <span
                            className="text-xs text-[#A29B95] whitespace-nowrap flex-shrink-0"
                            data-testid={id('draft-saved-note')}
                        >
                            Draft saved at {draftSavedAt}
                        </span>
                    )}
                </div>
            )}

            {/* The selector slot, available to EVERY mode, immediately above the
                editor — so what a template or signature produces lands in the
                body the user is looking at. */}
            {onSignatureChange && (
                <TemplateSignatureSelectors
                    idPrefix={composerId}
                    templates={templates}
                    adminTemplates={adminTemplates}
                    signatures={signatures}
                    adminSignatures={adminSignatures}
                    activeOverrideId={activeOverrideId}
                    user={user}
                    senderEmail={senderEmail}
                    onBodyChange={onBodyChange}
                    signature={signature}
                    onSignatureChange={onSignatureChange}
                    subject={subject}
                    onSubjectChange={onSubjectChange}
                    subjectMode={subjMode}
                    disabled={selectorsDisabled}
                    variant={page ? 'strip' : 'compact'}
                    ruleClassName={page ? undefined : RULE}
                />
            )}

            <div
                ref={editorRef}
                className={page ? 'flex-1 min-h-0 flex flex-col w-full' : 'w-full'}
            >
                <RichTextEditor
                    content={body}
                    onChange={onBodyChange}
                    placeholder={contract.placeholder}
                    minHeight={page ? '360px' : '110px'}
                    variant="flush"
                    className={page ? 'flex-1 min-h-0' : ''}
                    bodyClassName={page ? undefined : 'px-[14px] py-3'}
                    toolbarClassName={page ? undefined : `px-[14px] py-1.5 ${RULE} bg-white`}
                    /* The mail toolbar, not the campaign-authoring one. The
                       show* flags below still gate; the preset only filters. */
                    toolbar={toolbar}
                    showMergeVariables={showMergeVariables}
                    showAIWriter={showAIWriter}
                    showContentLibrary={showContentLibrary}
                />
            </div>

            {onAddAttachments && (
                <div className={page
                    ? 'flex-shrink-0 px-5 py-2 border-t border-[#EDEBE6]'
                    : `px-[14px] py-2 border-t ${RULE}`}
                >
                    <AttachmentBar
                        idPrefix={composerId}
                        attachments={attachments}
                        onAdd={onAddAttachments}
                        onRemove={onRemoveAttachment}
                        disabled={sending}
                    />
                </div>
            )}

            {!page && (
                /* The card's send row, as the prototype draws it: Discard on
                   the left (it IS close — the prototype's `closeReply`), Send
                   on the right, one hairline above. No identity note: in a
                   thread that is the footer's job. Send is a plain button —
                   scheduled send does not exist, and #623 says not to build a
                   split control around one item. */
                <div className={`flex items-center gap-2 px-[14px] py-[10px] border-t ${RULE}`}>
                    {onDiscard && (
                        <button
                            type="button"
                            onClick={onDiscard}
                            className="inline-flex items-center gap-[6px] h-8 px-2 rounded-[7px]
                                       text-[12.5px] text-[#57504B] hover:bg-[#F4F2EE]"
                            data-testid={id('composer-discard')}
                        >
                            <Trash2 className="w-[14px] h-[14px]" />
                            Discard
                        </button>
                    )}
                    {/* #970 — a signature-only reply is not a message: Send
                        stays off (no spinner) until something is typed. */}
                    <Button
                        size="sm"
                        className="ml-auto bg-stone-900 hover:bg-stone-800"
                        onClick={onSend}
                        disabled={sending || sendDisabled || !authored}
                        title={!sending && !sendDisabled && !authored ? 'Type a message first' : undefined}
                        data-testid={id('inbox-reply-send')}
                    >
                        {sending || sendDisabled
                            ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            : <Send className="w-4 h-4 mr-1" />}
                        {contract.sendLabel}
                    </Button>
                </div>
            )}
        </div>
    );
}
